import React, { useEffect, useMemo } from 'react';
import { Box, Heading, Page } from '@wix/design-system';
import { PageHeader, AppsSection } from '@wix/app-market-components';
import {
  appMarketSubCategoryClick,
  appMarketPageView,
  appMarketBackButtonClick,
  appMarketPageStartLoadedSrc24Evid163,
} from '@wix/bi-logger-app-market-data/v2';
import s from '../pages.scss';
import {
  useJunkYard,
  useEssentials,
  useServices,
  useRouterContext,
  useSiteContext,
  getPageViewBiData,
  QueryDecorator,
  categoryPageId,
  AppMarketPage,
  useComponentLoadReporter,
  usePageLoad,
  useAppsSectionSharedPropsMapper,
  convertStringToBi,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import type { CategoryPageData } from './api/category-page-data';
import { getCategoryPageData } from './api/category-page-data';
import { CategorySkeleton } from './category-skeleton';
import { BackButton } from '../../components/back-button';
import { withSaleBanner } from '../../components/sale-banner';

export interface CategoryPageComponentProps extends CategoryPageData {
  slug: string;
  onLoad: () => void;
}

function CategoryPageComponent({
  slug,
  category,
  appGroupSections,
  onLoad,
}: CategoryPageComponentProps) {
  useMemo(() => onLoad(), [slug]);
  const appsSectionSharedPropsMapper = useAppsSectionSharedPropsMapper();
  const { routerData } = useJunkYard();
  const { biLogger } = useEssentials();
  const { horizontalNav } = useSiteContext();
  const { router, route } = useRouterContext();
  const page = AppMarketPage.CATEGORY;

  usePageLoad({
    route,
    oldRoute: routerData.route,
    onLoad: () =>
      biLogger.report(
        appMarketPageView(
          getPageViewBiData({
            routerData,
            pageType: page,
            pageName: slug,
            subCategories: appGroupSections.map((section) => section.tagSlug),
          }),
        ),
      ),
  });

  function onBack() {
    biLogger.report(
      appMarketBackButtonClick({
        tag_type: page,
        tag_name: slug,
        location: 'collection_page',
      }),
    );
    router.navigateTo({
      path: RoutePath.BACK,
      payload: undefined,
    });
  }

  function onButtonClick(subCategorySlug: string, categorySlug: string) {
    biLogger.report(
      appMarketSubCategoryClick({
        type: 'view_more_apps',
        subcategory_name: convertStringToBi(subCategorySlug),
        category_name: convertStringToBi(category.slug),
      }),
    );
    router.navigateTo({
      path: RoutePath.SUB_CATEGORY,
      payload: {
        parentSlug: categorySlug,
        slug: subCategorySlug,
        subCat: subCategorySlug,
        referral: 'view_more_apps',
      },
    });
  }

  return (
    <Page className={`${s.appMarketPage} ${s.appMarketPageBackgroundColor}`}>
      <Page.Content>
        {horizontalNav ? (
          <Box marginTop="24px" gap="12px" verticalAlign="middle">
            {router.hasHistory() && (
              <BackButton
                size="small"
                weight="thin"
                skin="dark"
                onBack={onBack}
              />
            )}
            <Heading size="large" dataHook="category-page-title">
              {category.name}
            </Heading>
          </Box>
        ) : (
          <>
            {router.hasHistory() && (
              <Box marginTop="18px">
                <BackButton
                  size="small"
                  weight="thin"
                  skin="dark"
                  onBack={onBack}
                />
              </Box>
            )}
            <Box
              dataHook="collection-page-header"
              marginTop={router.hasHistory() ? '6px' : '18px'}
              direction="vertical"
            >
              <PageHeader
                primaryTitle={category.description}
                secondaryTitle={category.name}
                imageUrl={category.image}
              />
            </Box>
          </>
        )}
        {appGroupSections.map((appGroupSection, index) => {
          return (
            <Box direction="vertical" marginTop="36px">
              <AppsSection
                {...appsSectionSharedPropsMapper(
                  appGroupSection,
                  page,
                  slug,
                  index,
                  undefined,
                  undefined,
                  {
                    path: RoutePath.SUB_CATEGORY,
                    payload: {
                      parentSlug: category.slug,
                      slug: appGroupSection.tagSlug,
                      subCat: appGroupSection.tagSlug,
                      referral: 'view_more_apps',
                    },
                  },
                )}
                onButtonClick={() =>
                  onButtonClick(appGroupSection.tagSlug, category.slug)
                }
                titleSize="medium"
              />
            </Box>
          );
        })}
      </Page.Content>
    </Page>
  );
}

export interface CategoryPageProps {
  slug: string;
}

export const CategoryPage = withSaleBanner(function ({
  slug,
}: CategoryPageProps) {
  const reportLoadFinish = useComponentLoadReporter({
    componentId: categoryPageId,
    params: { categorySlug: slug },
    deps: [slug],
  });
  const { biLogger } = useEssentials();
  const { languageCode } = useSiteContext();
  const services = useServices();

  useEffect(() => {
    biLogger.report(
      appMarketPageStartLoadedSrc24Evid163({
        tag_type: AppMarketPage.CATEGORY,
      }),
    );
  }, [slug]);

  return (
    <QueryDecorator
      queryFn={() =>
        getCategoryPageData({
          services,
          slug,
          languageCode,
        })
      }
      queryKey={`${categoryPageId}-${slug}-${languageCode}`}
    >
      {({ data: categoryPageData, isLoading }) =>
        isLoading ? (
          <CategorySkeleton />
        ) : (
          <CategoryPageComponent
            {...categoryPageData}
            slug={slug}
            onLoad={reportLoadFinish}
          />
        )
      }
    </QueryDecorator>
  );
});
